import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  X, 
  Youtube, 
  MessageCircle, 
  PlayCircle, 
  ChevronRight,
  BookOpen,
  HelpCircle,
  Zap,
  Lightbulb,
  ArrowLeft,
  CheckCircle2,
  DollarSign,
  Bot,
  Crown,
  FileCheck,
  Receipt,
  Calendar,
  PenTool
} from 'lucide-react';
import { TUTORIAL_LINKS } from '../constants';
import HelpBotChat from './help/HelpBotChat';

interface HelpCenterModalProps {
  isOpen: boolean;
  onClose: () => void;
  userNiche?: string;
  userPlan?: string;
}

export default function HelpCenterModal({ isOpen, onClose, userNiche, userPlan }: HelpCenterModalProps) {
  const [activeView, setActiveView] = useState<'menu' | 'help-bot' | 'pricing-faq' | 'quick-guides' | 'plans-faq'>('menu');
  const youtubePlaylistUrl = "https://www.youtube.com/playlist?list=PL2rhild7W4eL9LgBrdxWpmrt7fpoRHdJr";
  const whatsappUrl = "https://wa.me/5511920897453?text=Olá,%20preciso%20de%20ajuda%20com%20o%20R-MAX";

  const handleClose = () => {
    setActiveView('menu');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-zinc-200 max-h-[calc(100dvh-2rem)] flex flex-col"
          >
            {/* VIEW 1: HELP BOT CHAT INTERATIVO */}
            {activeView === 'help-bot' && (
              <HelpBotChat 
                onBack={() => setActiveView('menu')}
                userNiche={userNiche}
                userPlan={userPlan}
              />
            )}

            {/* VIEW 2: MENU PRINCIPAL */}
            {activeView === 'menu' && (
              <>
                {/* Header */}
                <div className="bg-brand p-6 text-white relative">
                  <div className="absolute top-0 right-0 p-4">
                    <button 
                      onClick={handleClose}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors backdrop-blur-md cursor-pointer"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-white/20 p-2 rounded-xl backdrop-blur-md">
                      <BookOpen className="h-6 w-6" />
                    </div>
                    <h2 className="text-xl font-bold">Academia R-MAX</h2>
                  </div>
                  <p className="text-white/80 text-sm">
                    Sua central de conhecimento para dominar a gestão e a precificação inteligente.
                  </p>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4 bg-zinc-50/50 overflow-y-auto flex-1">
                  
                  {/* CARD DESTAQUE: TIRA-DÚVIDAS IA (R-MAX RESPONDE) */}
                  <button
                    onClick={() => setActiveView('help-bot')}
                    className="w-full text-left group block bg-linear-to-r from-emerald-600 to-teal-700 text-white rounded-2xl p-4 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer relative overflow-hidden active:scale-98"
                  >
                    <div className="absolute top-0 right-0 translate-x-4 -translate-y-4 w-24 h-24 bg-white/10 rounded-full blur-xl" />
                    <div className="flex items-center gap-4 relative z-10">
                      <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-transform backdrop-blur-xs">
                        <Bot className="h-7 w-7 text-emerald-200" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-xs font-black uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full text-white">Atendimento Rápido 24h</span>
                          <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
                        </div>
                        <h3 className="font-bold text-sm text-white">Tirar Dúvidas com o R-MAX</h3>
                        <p className="text-xs text-white/80 truncate">Pergunte qualquer coisa sobre o app e seus lucros.</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-white/70 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>

                  {/* YouTube Card */}
                  <a 
                    href={youtubePlaylistUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block bg-white rounded-2xl p-4 border border-zinc-100 shadow-xs hover:shadow-md hover:border-brand/30 transition-all duration-300"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center text-red-600 group-hover:scale-110 transition-transform">
                        <Youtube className="h-7 w-7" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-zinc-900 group-hover:text-brand transition-colors text-sm">Aprenda em Vídeo (Aulas)</h3>
                        <p className="text-xs text-zinc-500">Assista nossa playlist rápida e domine o R-MAX.</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-zinc-300 group-hover:text-brand transition-colors" />
                    </div>
                  </a>

                  {/* Plans FAQ Card (Novo: Entenda os 3 Planos) */}
                  <button 
                    onClick={() => setActiveView('plans-faq')}
                    className="w-full text-left group block bg-white rounded-2xl p-4 border border-zinc-100 shadow-xs hover:shadow-md hover:border-brand/30 transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 group-hover:scale-110 transition-transform">
                        <Crown className="h-7 w-7" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-zinc-900 group-hover:text-brand transition-colors text-sm">Entenda os 3 Planos</h3>
                        <p className="text-xs text-zinc-500">Diferenças entre Grátis, PRO e Especialista Premium.</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-zinc-300 group-hover:text-brand transition-colors" />
                    </div>
                  </button>

                  {/* Pricing Logic FAQ Card */}
                  <button 
                    onClick={() => setActiveView('pricing-faq')}
                    className="w-full text-left group block bg-white rounded-2xl p-4 border border-zinc-100 shadow-xs hover:shadow-md hover:border-brand/30 transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
                        <Lightbulb className="h-7 w-7" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-zinc-900 group-hover:text-brand transition-colors text-sm">Como Funciona o Cálculo do Lucro e da Hora</h3>
                        <p className="text-xs text-zinc-500">A matemática financeira que protege seu bolso.</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-zinc-300 group-hover:text-brand transition-colors" />
                    </div>
                  </button>

                  {/* Fichas Operacionais Rápidas (1 Minuto) */}
                  <button 
                    onClick={() => setActiveView('quick-guides')}
                    className="w-full text-left group block bg-white rounded-2xl p-4 border border-zinc-100 shadow-xs hover:shadow-md hover:border-brand/30 transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-brand group-hover:scale-110 transition-transform border border-emerald-100">
                        <BookOpen className="h-7 w-7" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-zinc-900 group-hover:text-brand transition-colors text-sm">Fichas Rápidas de Ação (1 Min)</h3>
                        <p className="text-xs text-zinc-500">Como usar OS com fotos, assinaturas, recibos e diárias.</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-zinc-300 group-hover:text-brand transition-colors" />
                    </div>
                  </button>

                  {/* WhatsApp Card */}
                  <a 
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block bg-white rounded-2xl p-4 border border-zinc-100 shadow-xs hover:shadow-md hover:border-brand/30 transition-all duration-300"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#25D366]/10 rounded-2xl flex items-center justify-center text-[#25D366] group-hover:scale-110 transition-transform">
                        <MessageCircle className="h-7 w-7" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-zinc-900 group-hover:text-brand transition-colors text-sm">Falar com Atendimento Humano</h3>
                        <p className="text-xs text-zinc-500">Dúvidas sobre sua conta ou pagamentos? Chame no WhatsApp.</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-zinc-300 group-hover:text-brand transition-colors" />
                    </div>
                  </a>

                  {/* Tutoriais Rápidos por Tela */}
                  <div className="bg-white rounded-2xl p-4 border border-zinc-100 shadow-xs space-y-4">
                    <h4 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest flex items-center gap-1.5 border-b border-zinc-100 pb-2">
                      <PlayCircle className="h-3.5 w-3.5 text-brand" />
                      Biblioteca de Tutoriais em Vídeo
                    </h4>
                    
                    {/* Categoria: Visão Geral */}
                    <div className="space-y-1.5">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Visão Geral & Dúvidas</span>
                      <div className="grid grid-cols-2 gap-2">
                        <a 
                          href={TUTORIAL_LINKS.tutorialCompleto}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2.5 rounded-xl bg-red-50 border border-red-100 hover:border-red-300 hover:bg-red-100/60 transition-all text-xs font-bold text-red-700 active:scale-[0.98]"
                        >
                          <div className="w-6 h-6 rounded-lg bg-red-500 text-white flex items-center justify-center shrink-0">
                            <PlayCircle className="h-3.5 w-3.5" />
                          </div>
                          <span className="truncate">Guia Definitivo</span>
                        </a>

                        <a 
                          href={TUTORIAL_LINKS.faqResponde}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2.5 rounded-xl bg-amber-50 border border-amber-100 hover:border-amber-300 hover:bg-amber-100/60 transition-all text-xs font-bold text-amber-700 active:scale-[0.98]"
                        >
                          <div className="w-6 h-6 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
                            <PlayCircle className="h-3.5 w-3.5" />
                          </div>
                          <span className="truncate">R-Max Responde</span>
                        </a>
                      </div>
                    </div>

                    {/* Categoria: Ferramentas do Especialista */}
                    <div className="space-y-1.5 pt-1">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-amber-600 flex items-center gap-1">
                        <Crown className="h-3 w-3" />
                        Ferramentas do Especialista
                      </span>
                      <div className="grid grid-cols-2 gap-2">
                        <a 
                          href={TUTORIAL_LINKS.assinaturaDigital}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2.5 rounded-xl bg-amber-50 border border-amber-200/80 hover:border-amber-400 hover:bg-amber-100/70 transition-all text-xs font-bold text-amber-900 active:scale-[0.98]"
                        >
                          <div className="w-6 h-6 rounded-lg bg-amber-600 text-white flex items-center justify-center shrink-0">
                            <PenTool className="h-3.5 w-3.5" />
                          </div>
                          <span className="truncate">Assinatura na Tela</span>
                        </a>

                        <a 
                          href={TUTORIAL_LINKS.ordemServicoFotos}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2.5 rounded-xl bg-amber-50 border border-amber-200/80 hover:border-amber-400 hover:bg-amber-100/70 transition-all text-xs font-bold text-amber-900 active:scale-[0.98]"
                        >
                          <div className="w-6 h-6 rounded-lg bg-amber-600 text-white flex items-center justify-center shrink-0">
                            <FileCheck className="h-3.5 w-3.5" />
                          </div>
                          <span className="truncate">OS com Fotos</span>
                        </a>

                        <a 
                          href={TUTORIAL_LINKS.recibosPix}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2.5 rounded-xl bg-amber-50 border border-amber-200/80 hover:border-amber-400 hover:bg-amber-100/70 transition-all text-xs font-bold text-amber-900 active:scale-[0.98]"
                        >
                          <div className="w-6 h-6 rounded-lg bg-amber-600 text-white flex items-center justify-center shrink-0">
                            <Receipt className="h-3.5 w-3.5" />
                          </div>
                          <span className="truncate">Recibos PIX</span>
                        </a>

                        <a 
                          href={TUTORIAL_LINKS.agendaMultiAtendentes}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2.5 rounded-xl bg-amber-50 border border-amber-200/80 hover:border-amber-400 hover:bg-amber-100/70 transition-all text-xs font-bold text-amber-900 active:scale-[0.98]"
                        >
                          <div className="w-6 h-6 rounded-lg bg-amber-600 text-white flex items-center justify-center shrink-0">
                            <Calendar className="h-3.5 w-3.5" />
                          </div>
                          <span className="truncate">Agenda & Vitrine</span>
                        </a>
                      </div>
                    </div>

                    {/* Categoria: Custos & Catálogos */}
                    <div className="space-y-1.5 pt-1">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Custos & Catálogos</span>
                      <div className="grid grid-cols-2 gap-2">
                        <a 
                          href={TUTORIAL_LINKS.custos}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2.5 rounded-xl bg-emerald-50 border border-emerald-100 hover:border-emerald-300 hover:bg-emerald-100/60 transition-all text-xs font-bold text-emerald-700 active:scale-[0.98]"
                        >
                          <div className="w-6 h-6 rounded-lg bg-emerald-500 text-white flex items-center justify-center shrink-0">
                            <PlayCircle className="h-3.5 w-3.5" />
                          </div>
                          <span className="truncate">Materiais & Insumos</span>
                        </a>

                        <a 
                          href={TUTORIAL_LINKS.receitas}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2.5 rounded-xl bg-emerald-50 border border-emerald-100 hover:border-emerald-300 hover:bg-emerald-100/60 transition-all text-xs font-bold text-emerald-700 active:scale-[0.98]"
                        >
                          <div className="w-6 h-6 rounded-lg bg-emerald-500 text-white flex items-center justify-center shrink-0">
                            <PlayCircle className="h-3.5 w-3.5" />
                          </div>
                          <span className="truncate">Receitas & Kits</span>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 text-center">
                    <p className="text-[10px] text-zinc-400 font-medium uppercase tracking-widest flex items-center justify-center gap-2">
                      <Zap className="h-3 w-3" />
                      Evoluindo seu negócio com o R-MAX
                      <Zap className="h-3 w-3" />
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-white border-t border-zinc-100 flex justify-center">
                  <button 
                    onClick={handleClose}
                    className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold rounded-xl border border-slate-200/60 transition-colors min-h-11 flex items-center justify-center cursor-pointer"
                  >
                    Fechar
                  </button>
                </div>
              </>
            )}

            {/* VIEW 3: ENTENDA OS 3 PLANOS */}
            {activeView === 'plans-faq' && (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex flex-col flex-1 min-h-0 bg-zinc-50"
              >
                <div className="bg-brand p-4 text-white flex items-center gap-3 shrink-0">
                  <button 
                    onClick={() => setActiveView('menu')} 
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <h2 className="text-base font-bold flex-1 text-center pr-8">Os 3 Planos do R-MAX</h2>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 space-y-4 text-zinc-700 text-xs">
                  {/* Plano Grátis */}
                  <div className="bg-white p-4 rounded-2xl border border-zinc-200 shadow-xs space-y-2">
                    <div className="flex items-center justify-between border-b border-zinc-100 pb-2">
                      <span className="font-bold text-zinc-900 text-sm">Plano Gratuito</span>
                      <span className="font-black text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded-md text-[10px]">R$ 0,00</span>
                    </div>
                    <p className="text-zinc-600 leading-relaxed">
                      Ideal para quem está começando e quer testar o sistema. Dá direito a <strong>5 orçamentos por mês</strong> com cálculo básico de hora base e envio de PDFs no WhatsApp.
                    </p>
                  </div>

                  {/* Plano PRO */}
                  <div className="bg-white p-4 rounded-2xl border border-emerald-200 shadow-xs space-y-2 relative overflow-hidden">
                    <div className="flex items-center justify-between border-b border-zinc-100 pb-2">
                      <div className="flex items-center gap-1.5">
                        <Zap className="h-4 w-4 text-emerald-600" />
                        <span className="font-bold text-zinc-900 text-sm">Plano PRO</span>
                      </div>
                      <span className="font-black text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md text-[10px]">R$ 19,90/mês</span>
                    </div>
                    <p className="text-zinc-600 leading-relaxed">
                      Para o profissional autônomo individual. Libera <strong>Orçamentos Ilimitados em PDF</strong>, <strong>Centro de Custos</strong> (Insumos, Receitas e Serviços), <strong>Histórico Completo</strong> e <strong>Radar de Preços</strong> da sua região.
                    </p>
                  </div>

                  {/* Plano Especialista Premium */}
                  <div className="bg-white p-4 rounded-2xl border border-amber-200 shadow-xs space-y-2 bg-linear-to-br from-white to-amber-50/40">
                    <div className="flex items-center justify-between border-b border-amber-100 pb-2">
                      <div className="flex items-center gap-1.5">
                        <Crown className="h-4 w-4 text-amber-600" />
                        <span className="font-bold text-zinc-900 text-sm">Especialista Premium</span>
                      </div>
                      <span className="font-black text-amber-800 bg-amber-100 px-2 py-0.5 rounded-md text-[10px]">R$ 49,90/mês</span>
                    </div>
                    <p className="text-zinc-600 leading-relaxed">
                      Para empresas e prestadores que querem organizar tudo no mesmo lugar. Libera tudo do PRO mais <strong>Assinatura Digital no Celular</strong>, <strong>Ordens de Serviço com Fotos</strong>, <strong>Recibos com QR Code PIX</strong>, <strong>Agenda de Horários</strong> e <strong>Cadastro de Clientes com rotas no mapa</strong>.
                    </p>
                  </div>

                  {/* Vantagem de Migração */}
                  <div className="p-3.5 bg-blue-50 border border-blue-200 rounded-2xl text-blue-900 space-y-1">
                    <p className="font-bold text-[11px] flex items-center gap-1">
                      <Zap className="h-3.5 w-3.5 text-blue-600" />
                      Regra de Migração PRO para Especialista:
                    </p>
                    <p className="text-[10px] text-blue-800 leading-relaxed">
                      Quem já é assinante PRO pode migrar para o Especialista a qualquer momento e usar imediatamente todos os novos recursos, pagando a diferença proporcional somente no próximo ciclo.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* VIEW 4: PRICING FAQ VIEW */}
            {activeView === 'pricing-faq' && (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex flex-col flex-1 min-h-0 bg-zinc-50"
              >
                <div className="bg-brand p-4 text-white flex items-center gap-3 shrink-0">
                  <button 
                    onClick={() => setActiveView('menu')} 
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <h2 className="text-base font-bold flex-1 text-center pr-8">Como Funciona a Precificação</h2>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 space-y-4 text-zinc-700">
                  <div className="space-y-4">
                    <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-xs">
                      <div className="flex items-center gap-3 mb-3 border-b border-zinc-100 pb-3">
                        <div className="bg-blue-100 p-2 rounded-xl"><DollarSign className="h-5 w-5 text-blue-600" /></div>
                        <h4 className="font-bold text-zinc-900 text-sm">1. Valor da sua Hora de Trabalho</h4>
                      </div>
                      <p className="text-xs text-zinc-600 leading-relaxed">
                        O valor da sua hora representa o custo do seu tempo. Ele cobre o seu <strong>Salário (Pró-labore)</strong> somado aos seus <strong>Custos Fixos do Mês</strong> (aluguel, ferramentas, luz, MEI), dividido pelas horas reais que você trabalha no mês.
                      </p>
                    </div>

                    <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-xs">
                      <div className="flex items-center gap-3 mb-3 border-b border-zinc-100 pb-3">
                        <div className="bg-amber-100 p-2 rounded-xl"><Lightbulb className="h-5 w-5 text-amber-600" /></div>
                        <h4 className="font-bold text-zinc-900 text-sm">2. Custos de Venda (Despesas)</h4>
                      </div>
                      <p className="text-xs text-zinc-600 leading-relaxed mb-4">
                        São os valores percentuais que você é obrigado a pagar por cada venda feita. O sistema repassa esses valores ao preço final automaticamente.
                      </p>
                      <ul className="text-xs space-y-3 text-zinc-600 bg-zinc-50 p-3 rounded-xl">
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-amber-500 shrink-0"/><span><strong>Taxa de Maquininha:</strong> O percentual descontado pela operadora do cartão.</span></li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-amber-500 shrink-0"/><span><strong>Comissão Padrão:</strong> Percentual repassado para vendedores ou ajudantes.</span></li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-amber-500 shrink-0"/><span><strong>Impostos:</strong> Alíquota recolhida na emissão da sua nota fiscal.</span></li>
                      </ul>
                    </div>

                    <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-xs border-l-4 border-l-emerald-500 relative overflow-hidden">
                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-3 border-b border-zinc-100 pb-3">
                          <div className="bg-emerald-100 p-2 rounded-xl"><Zap className="h-5 w-5 text-emerald-600" /></div>
                          <h4 className="font-bold text-zinc-900 text-sm">3. Margem de Lucro Real</h4>
                        </div>
                        <p className="text-xs text-zinc-600 leading-relaxed">
                          O R-MAX garante que seu lucro nunca seja diminuído pelas taxas.
                          <br/><br/>
                          Isso significa que o sistema soma todas as taxas de venda e calcula o preço exato para que, <strong>mesmo após pagar os impostos e a maquininha, o seu lucro desejado fique 100% no seu bolso</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* VIEW 5: FICHAS DE AÇÃO RÁPIDA (1 Minuto) */}
            {activeView === 'quick-guides' && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex flex-col h-full overflow-hidden"
              >
                {/* Header */}
                <div className="bg-brand p-6 text-white relative">
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setActiveView('menu')}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors backdrop-blur-md cursor-pointer"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                      <h2 className="text-xl font-bold">Fichas de Ação Rápida</h2>
                      <p className="text-white/80 text-xs">Consultas práticas de 1 minuto para o dia a dia.</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4 bg-zinc-50 overflow-y-auto flex-1 text-xs">
                  {/* Ficha 1 */}
                  <div className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-xs space-y-2">
                    <span className="text-[9px] font-black uppercase tracking-widest text-brand bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                      Ficha #1 • Viagens & Deslocamento
                    </span>
                    <h4 className="font-black text-zinc-900 text-sm">Como Cobrar Diárias e Serviços Fora da Cidade?</h4>
                    <p className="text-zinc-600 leading-relaxed font-medium">
                      Nunca cobre apenas a gasolina! Para serviços fora da sua base, some:
                    </p>
                    <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-100 space-y-1.5 text-zinc-700 font-medium">
                      <p><strong>1. Horas de Trânsito:</strong> Multiplique o tempo de ida e volta pelo valor da sua hora de trabalho.</p>
                      <p><strong>2. Desgaste Veicular (Km):</strong> Adicione R$ 1,20 a R$ 1,80 por km rodado (cobre combustível + pneus + manutenção).</p>
                      <p><strong>3. Alimentação & Hospedagem:</strong> Custo integral direto sem desconto.</p>
                    </div>
                  </div>

                  {/* Ficha 2 */}
                  <div className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-xs space-y-2">
                    <span className="text-[9px] font-black uppercase tracking-widest text-brand bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                      Ficha #2 • Assinatura do Cliente no Celular
                    </span>
                    <h4 className="font-black text-zinc-900 text-sm">Como Colher a Assinatura do Cliente na Tela?</h4>
                    <p className="text-zinc-600 leading-relaxed font-medium">
                      Na entrega do serviço, abra a OS ou Proposta e toque no ícone de caneta. Entregue o celular para o cliente assinar com o dedo ou caneta stylus. O R-MAX carimba a data, hora e código de autenticidade digital no PDF, gerando validade jurídica incontestável.
                    </p>
                  </div>

                  {/* Ficha 3 */}
                  <div className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-xs space-y-2">
                    <span className="text-[9px] font-black uppercase tracking-widest text-brand bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                      Ficha #3 • Vistoria Fotográfica na OS
                    </span>
                    <h4 className="font-black text-zinc-900 text-sm">Como Evitar Reclamações com Fotos de Vistoria?</h4>
                    <p className="text-zinc-600 leading-relaxed font-medium">
                      Tire sempre 3 fotos: (1) O estado inicial do equipamento/obra antes de começar, (2) Uma foto do processo e (3) A entrega concluída. As fotos ficam salvas na OS para comprovar a qualidade do serviço.
                    </p>
                  </div>

                  {/* Ficha 4 */}
                  <div className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-xs space-y-2">
                    <span className="text-[9px] font-black uppercase tracking-widest text-brand bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                      Ficha #4 • Recibo PIX em Segundos
                    </span>
                    <h4 className="font-black text-zinc-900 text-sm">Como Emitir Recibo com QR Code PIX?</h4>
                    <p className="text-zinc-600 leading-relaxed font-medium">
                      Acesse "Recibos PIX", preencha o nome do pagador e o valor. O sistema gera na hora o PDF com QR Code legível para o cliente escanear e transferir o saldo na entrega do trabalho.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
